/**
 * CorpoCard V3 — Glass panel with 3D tilt on hover.
 *
 * V2 hardcodeaba el chaflán de 12px y cuatro corchetes de esquina, así que
 * todos los temas compartían exactamente la misma forma y solo cambiaban de
 * color. Ahora la geometría sale de --bw-corner-cut / --bw-radius-lg y el
 * remate de <Ornament>, que lee theme.ornament.
 */

import { type ReactNode } from "react";
import { useTilt } from "../../hooks/useTilt";
import { Ornament } from "./Ornament";

/** Chaflán corpo. Con --bw-corner-cut: 0px degenera en rectángulo y manda
 *  el border-radius — así una sola regla sirve para Arasaka y Niebla Gris. */
const CLIP =
  "polygon(0 0, calc(100% - var(--bw-corner-cut)) 0, 100% var(--bw-corner-cut), 100% 100%, var(--bw-corner-cut) 100%, 0 calc(100% - var(--bw-corner-cut)))";

interface CorpoCardProps {
  children: ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
  accent?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
  elevation?: 1 | 2 | 3;
  tilt?: boolean;
  /** Overline sobre el título — contexto corto en mayúsculas. */
  eyebrow?: string;
  /** Acción alineada a la derecha del encabezado. */
  action?: ReactNode;
  /** Oculta los remates de esquina (útil en tiles densos). */
  bare?: boolean;
}

export function CorpoCard({
  children,
  className = "",
  title,
  subtitle,
  accent = false,
  onClick,
  style,
  elevation = 1,
  tilt = false,
  eyebrow,
  action,
  bare = false,
}: CorpoCardProps) {
  const { ref, style: tiltStyle, handleMouseMove, handleMouseLeave } = useTilt(2.5);
  const useTiltEffect = tilt || !!onClick;

  return (
    <div
      ref={useTiltEffect ? ref : undefined}
      onMouseMove={useTiltEffect ? handleMouseMove : undefined}
      onMouseLeave={useTiltEffect ? handleMouseLeave : undefined}
      className={`
        relative p-4
        ${accent ? "border-l-2 border-l-[var(--bw-accent)]" : ""}
        ${onClick ? "cursor-pointer" : ""}
        ${className}
      `}
      style={{
        background: "var(--bw-glass-bg)",
        backdropFilter: "blur(var(--bw-glass-blur))",
        WebkitBackdropFilter: "blur(var(--bw-glass-blur))",
        border: "1px solid var(--bw-glass-border)",
        boxShadow: `var(--bw-elevation-${elevation})`,
        borderRadius: "var(--bw-radius-lg)",
        clipPath: CLIP,
        ...(useTiltEffect ? tiltStyle : {}),
        ...style,
      }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Shine overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background: "var(--bw-glass-shine)",
          borderRadius: "var(--bw-radius-lg)",
          clipPath: CLIP,
        }}
      />

      {/* Remate de esquina — la forma la decide el tema */}
      {!bare && (
        <>
          <Ornament corner="tl" />
          <Ornament corner="br" />
        </>
      )}

      {/* Encabezado */}
      {(title || subtitle || eyebrow) && (
        <div className="relative mb-3 pb-2 border-b border-[var(--bw-glass-border)] flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {eyebrow && <p className="bw-eyebrow text-[var(--bw-accent)] mb-1">{eyebrow}</p>}
            {title && (
              <h3 className="bw-display text-[var(--bw-fg)] text-[17px] md:text-[19px]">
                {title}
              </h3>
            )}
            {subtitle && <p className="bw-prose text-[13px] text-[var(--bw-muted)] mt-1">{subtitle}</p>}
          </div>
          {action && <div className="flex-shrink-0">{action}</div>}
        </div>
      )}

      <div className="relative">{children}</div>
    </div>
  );
}
